import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  CREST_INNER,
  CREST_OUTER,
  CREST_VIEWBOX,
} from "../BrandCrest/paths";

const HEADER_OFFSET = 80;

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (!element) return false;
  // Contact is a full-viewport section with its own top padding for the nav.
  const offset = id === "contact" ? 0 : HEADER_OFFSET;
  const offsetPosition =
    element.getBoundingClientRect().top + window.pageYOffset - offset;
  window.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  });
  return true;
};

const waitForSection = (id, onFound) => {
  if (scrollToSection(id)) {
    onFound();
    return () => {};
  }
  let tries = 0;
  const interval = setInterval(() => {
    tries += 1;
    if (scrollToSection(id) || tries > 150) {
      clearInterval(interval);
      if (tries <= 150) onFound();
    }
  }, 50);
  return () => clearInterval(interval);
};

const navLinkStyles = `
  relative 
  text-white 
  hover:text-lime-400 
  transition-colors
  after:content-['']
  after:absolute
  after:w-full
  after:scale-x-0
  after:h-0.5
  after:bottom-0
  after:left-0
  after:bg-lime-400
  after:origin-right
  after:transition-transform
  after:duration-300
  hover:after:scale-x-100
  hover:after:origin-left
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const location = useLocation();
  const sectionIds = ["home", "about", "services", "work", "lab", "contact"];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!location.hash) return undefined;
    const id = location.hash.replace("#", "");
    setActiveSection(id);
    return waitForSection(id, () => setActiveSection(id));
  }, [location]);

  useEffect(() => {
    if (location.pathname !== "/") {
      return undefined;
    }

    const updateActiveSection = () => {
      const scrollAnchor = window.scrollY + 140;
      let currentSection = sectionIds[0];

      sectionIds.forEach((id) => {
        const section = document.getElementById(id);
        if (section && scrollAnchor >= section.offsetTop) {
          currentSection = id;
        }
      });

      setActiveSection(currentSection);
    };

    updateActiveSection();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const getDesktopSectionClass = (sectionId) => {
    const isActive = location.pathname === "/" && activeSection === sectionId;
    return `${navLinkStyles} ${
      isActive ? "text-lime-400 after:scale-x-100 after:origin-left" : ""
    }`;
  };

  const getMobileSectionClass = (sectionId) => {
    const isActive = location.pathname === "/" && activeSection === sectionId;
    return `site-mobile-menu__link${isActive ? " is-active" : ""}`;
  };

const handleNavigation = (e, sectionId) => {
  e.preventDefault();
  setActiveSection(sectionId);

  if (location.pathname !== "/") {
    window.location.href = `/#${sectionId}`;
    return;
  }

  // First close the mobile menu
  setIsMenuOpen(false);
  
  setTimeout(() => waitForSection(sectionId, () => {}), 360);
};

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top)] transition-all duration-300 ${
        isScrolled || isMenuOpen
          ? "bg-black/95 backdrop-blur-sm shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center"
            aria-label="Webtrova home"
            onClick={() => {
              window.scrollTo(0, 0);
              setIsMenuOpen(false);
            }}
          >
            <svg
              viewBox={CREST_VIEWBOX}
              width="44"
              height="48"
              className="h-12 w-11 shrink-0 text-white"
              aria-hidden="true"
            >
              <path d={CREST_OUTER} fill="currentColor" fillRule="evenodd" />
              <path d={CREST_INNER} fill="currentColor" fillRule="evenodd" />
            </svg>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {location.pathname === "/" ? (
              <>
                <a
                  href="#home"
                  onClick={(e) => handleNavigation(e, "home")}
                  className={getDesktopSectionClass("home")}
                >
                  HOME
                </a>
                <a
                  href="#about"
                  onClick={(e) => handleNavigation(e, "about")}
                  className={getDesktopSectionClass("about")}
                >
                  ABOUT ME
                </a>
                <a
                  href="#services"
                  onClick={(e) => handleNavigation(e, "services")}
                  className={getDesktopSectionClass("services")}
                >
                  SERVICES
                </a>
                <a
                  href="#work"
                  onClick={(e) => handleNavigation(e, "work")}
                  className={getDesktopSectionClass("work")}
                >
                  MY WORK
                </a>
                <a
                  href="#lab"
                  onClick={(e) => handleNavigation(e, "lab")}
                  className={getDesktopSectionClass("lab")}
                >
                  THE LAB
                </a>
                <a
                  href="#contact"
                  onClick={(e) => handleNavigation(e, "contact")}
                  className={getDesktopSectionClass("contact")}
                >
                  CONTACT
                </a>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  onClick={() => window.scrollTo(0, 0)}
                  className={navLinkStyles}
                >
                  HOME
                </Link>
                <Link to="/about" className={navLinkStyles}>
                  ABOUT ME
                </Link>
                <Link to="/services" className={navLinkStyles}>
                  SERVICES
                </Link>
                <Link to="/projects" className={navLinkStyles}>
                  MY WORK
                </Link>
                <Link to="/lab" className={navLinkStyles}>
                  THE LAB
                </Link>
                <Link
                  to="/contact"
                  onClick={() => {
                    setTimeout(() => {
                      const element = document.getElementById("contact");
                      if (element) {
                        const headerHeight = 80;
                        const elementPosition =
                          element.getBoundingClientRect().top;
                        const offsetPosition =
                          elementPosition + window.pageYOffset - headerHeight;

                        window.scrollTo({
                          top: offsetPosition,
                          behavior: "smooth",
                        });
                      }
                    }, 100);
                  }}
                  className={navLinkStyles}
                >
                  CONTACT
                </Link>
              </>
            )}
          </nav>

          <button
            type="button"
            className="hamburger relative md:hidden flex h-11 w-11 items-center justify-center rounded-lg"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            <span
              className={`hamburger__icon${isMenuOpen ? " is-open" : ""}`}
              aria-hidden="true"
            >
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>

        <div
          id="mobile-menu"
          className={`site-mobile-menu md:hidden absolute left-0 right-0${
            isMenuOpen ? " is-open" : ""
          }`}
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className="site-mobile-menu__clip">
          <nav className="site-mobile-menu__panel flex flex-col gap-1">
            {location.pathname === "/" ? (
              <>
                <a
                  href="#home"
                  onClick={(e) => handleNavigation(e, "home")}
                  className={getMobileSectionClass("home")}
                >
                  HOME
                </a>
                <a
                  href="#about"
                  onClick={(e) => handleNavigation(e, "about")}
                  className={getMobileSectionClass("about")}
                >
                  ABOUT ME
                </a>
                <a
                  href="#services"
                  onClick={(e) => handleNavigation(e, "services")}
                  className={getMobileSectionClass("services")}
                >
                  SERVICES
                </a>
                <a
                  href="#work"
                  onClick={(e) => handleNavigation(e, "work")}
                  className={getMobileSectionClass("work")}
                >
                  MY WORK
                </a>
                <a
                  href="#lab"
                  onClick={(e) => handleNavigation(e, "lab")}
                  className={getMobileSectionClass("lab")}
                >
                  THE LAB
                </a>
                <a
                  href="#contact"
                  onClick={(e) => handleNavigation(e, "contact")}
                  className={getMobileSectionClass("contact")}
                >
                  CONTACT
                </a>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  className="site-mobile-menu__link"
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.scrollTo(0, 0);
                  }}
                >
                  HOME
                </Link>
                <Link
                  to="/about"
                  className="site-mobile-menu__link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  ABOUT ME
                </Link>
                <Link
                  to="/projects"
                  className="site-mobile-menu__link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  MY WORK
                </Link>
                <Link
                  to="/services"
                  className="site-mobile-menu__link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  SERVICES
                </Link>
                <Link
                  to="/lab"
                  className="site-mobile-menu__link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  THE LAB
                </Link>
                <Link
                  to="/contact"
                  className="site-mobile-menu__link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  CONTACT
                </Link>
              </>
            )}
          </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
